import React, { useState } from 'react';
import { Check, X, Eye, FileText, MapPin, Calendar, Clock, Star, Building, User, Phone, Mail, Filter, Search } from 'lucide-react';

const ProviderVerification = ({ providers, setProviders }) => {
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);

  // Filter providers based on type, status, and search term
  const filteredProviders = providers?.filter(provider => {
    const matchesType = filterType === 'all' || provider?.type === filterType;
    const matchesStatus = filterStatus === 'all' || provider?.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      provider?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      provider?.businessName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      provider?.contactPerson?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    return matchesType && matchesStatus && matchesSearch;
  }) || [];

  // Get provider type display name
  const getProviderTypeLabel = (type) => {
    const labels = {
      technician: 'Technician',
      restaurant: 'Restaurant',
      delivery: 'Delivery Partner',
      service: 'Service Provider'
    };
    return labels?.[type] || 'Service Provider';
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      under_review: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return badges?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Get verification stage badge
  const getVerificationStageBadge = (stage) => {
    const badges = {
      documents: 'bg-orange-100 text-orange-800 border-orange-200',
      compliance: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200'
    };
    return badges?.[stage] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Get document status icon
  const getDocumentStatusIcon = (status) => {
    switch(status) {
      case 'verified':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'rejected':
        return <X className="w-4 h-4 text-red-600" />;
      case 'uploaded':
        return <FileText className="w-4 h-4 text-blue-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  // Handle provider approval
  const handleApprove = (providerId) => {
    setProviders(prev => prev?.map(provider => 
      provider?.id === providerId 
        ? { ...provider, status: 'approved', verificationStage: 'completed', updatedAt: new Date()?.toISOString()?.split('T')?.[0] }
        : provider
    ) || []);
    setShowModal(false);
  };

  // Handle provider rejection
  const handleReject = (providerId, reason = 'Verification requirements not met') => {
    setProviders(prev => prev?.map(provider => 
      provider?.id === providerId 
        ? { ...provider, status: 'rejected', rejectionReason: reason, updatedAt: new Date()?.toISOString()?.split('T')?.[0] }
        : provider
    ) || []);
    setShowModal(false);
  };

  // Handle view provider details
  const handleViewDetails = (provider) => {
    setSelectedProvider(provider);
    setShowModal(true);
  };

  // Get provider statistics
  const getStats = () => {
    const total = providers?.length || 0;
    const pending = providers?.filter(p => p?.status === 'pending')?.length || 0;
    const approved = providers?.filter(p => p?.status === 'approved')?.length || 0;
    const rejected = providers?.filter(p => p?.status === 'rejected')?.length || 0;
    
    return { total, pending, approved, rejected };
  };

  const stats = getStats();

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Provider Verification</h2>
            <p className="text-sm text-muted-foreground">Verify and approve service providers, technicians, restaurants, and delivery partners</p>
          </div>
          
          {/* Statistics Cards */}
          <div className="flex gap-4">
            <div className="bg-background border border-border rounded-lg px-4 py-2">
              <div className="text-2xl font-bold text-foreground">{stats?.total}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
              <div className="text-2xl font-bold text-yellow-800">{stats?.pending}</div>
              <div className="text-xs text-yellow-600">Pending</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
              <div className="text-2xl font-bold text-green-800">{stats?.approved}</div>
              <div className="text-xs text-green-600">Approved</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              <div className="text-2xl font-bold text-red-800">{stats?.rejected}</div>
              <div className="text-xs text-red-600">Rejected</div>
            </div>
          </div>
        </div>
      </div>
      {/* Filters and Search */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="border border-border rounded-md px-3 py-2 text-sm bg-background"
            >
              <option value="all">All Types</option>
              <option value="technician">Technicians</option>
              <option value="restaurant">Restaurants</option>
              <option value="delivery">Delivery Partners</option>
              <option value="service">Service Providers</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="border border-border rounded-md px-3 py-2 text-sm bg-background"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search providers by name, business name, or contact person..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md text-sm bg-background"
            />
          </div>
        </div>
      </div>
      {/* Providers List */}
      <div className="flex-1 overflow-auto p-6">
        <div className="grid gap-6">
          {filteredProviders?.length > 0 ? (
            filteredProviders?.map(provider => (
              <div key={provider?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Building className="w-5 h-5 text-muted-foreground" />
                      <h3 className="text-lg font-semibold text-foreground">{provider?.name || 'Unknown Provider'}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(provider?.status)}`}>
                        {provider?.status?.toUpperCase() || 'PENDING'}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getVerificationStageBadge(provider?.verificationStage)}`}>
                        {provider?.verificationStage?.toUpperCase() || 'DOCUMENTS'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          <span className="font-medium">Type:</span>
                          <span className="text-muted-foreground">{getProviderTypeLabel(provider?.type)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">Contact:</span>
                          <span className="text-muted-foreground">{provider?.contactPerson || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">Email:</span>
                          <span className="text-muted-foreground">{provider?.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">Phone:</span>
                          <span className="text-muted-foreground">{provider?.phone || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">Location:</span>
                          <span className="text-muted-foreground">{provider?.location || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">Applied:</span>
                          <span className="text-muted-foreground">{provider?.createdAt || 'N/A'}</span>
                        </div>
                        {provider?.rating && (
                          <div className="flex items-center gap-2 text-sm">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span className="font-medium">Rating:</span>
                            <span className="text-muted-foreground">{provider?.rating}/5.0</span>
                          </div>
                        )}
                        {provider?.rejectionReason && (
                          <div className="text-sm text-red-600">
                            <span className="font-medium">Rejection Reason:</span> {provider?.rejectionReason}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Services */}
                    <div className="mb-4">
                      <span className="text-sm font-medium text-foreground">Services:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {provider?.services?.map((service, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs">
                            {service}
                          </span>
                        )) || []}
                      </div>
                    </div>

                    {/* Documents Status */}
                    <div className="mb-4">
                      <span className="text-sm font-medium text-foreground">Documents:</span>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {provider?.documents && Object.entries(provider?.documents)?.map(([docType, doc]) => (
                          <div key={docType} className="flex items-center gap-2 text-sm">
                            {getDocumentStatusIcon(doc?.status)}
                            <span className="capitalize">{docType?.replace(/([A-Z])/g, ' $1')?.trim()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => handleViewDetails(provider)}
                      className="flex items-center gap-2 px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </button>
                    
                    {provider?.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(provider?.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                        >
                          <Check className="w-4 h-4" />
                          Approve
                        </button>
                        
                        <button
                          onClick={() => handleReject(provider?.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                        >
                          <X className="w-4 h-4" />
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Building className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No providers found</h3>
              <p className="text-muted-foreground">
                {searchTerm || filterType !== 'all' || filterStatus !== 'all' ?'Try adjusting your search filters' :'No providers have applied for verification yet'
                }
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Provider Details Modal */}
      {showModal && selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-foreground">Provider Details</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-3">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="font-medium">Business Name:</span> {selectedProvider?.businessName || 'N/A'}</div>
                  <div><span className="font-medium">Provider Type:</span> {getProviderTypeLabel(selectedProvider?.type)}</div>
                  <div><span className="font-medium">Contact Person:</span> {selectedProvider?.contactPerson || 'N/A'}</div>
                  <div><span className="font-medium">Email:</span> {selectedProvider?.email || 'N/A'}</div>
                  <div><span className="font-medium">Phone:</span> {selectedProvider?.phone || 'N/A'}</div>
                  <div><span className="font-medium">Location:</span> {selectedProvider?.location || 'N/A'}</div>
                  <div><span className="font-medium">Application Date:</span> {selectedProvider?.createdAt || 'N/A'}</div>
                  <div><span className="font-medium">Last Updated:</span> {selectedProvider?.updatedAt || 'N/A'}</div>
                </div>
              </div>

              {/* Services */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-3">Services Offered</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProvider?.services?.map((service, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-md text-sm">
                      {service}
                    </span>
                  )) || <span className="text-muted-foreground">No services listed</span>}
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-3">Documents</h4>
                <div className="space-y-3">
                  {selectedProvider?.documents && Object.entries(selectedProvider?.documents)?.map(([docType, doc]) => (
                    <div key={docType} className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div className="flex items-center gap-3">
                        {getDocumentStatusIcon(doc?.status)}
                        <div>
                          <div className="font-medium capitalize">{docType?.replace(/([A-Z])/g, ' $1')?.trim()}</div>
                          <div className="text-sm text-muted-foreground">Status: {doc?.status || 'pending'}</div>
                        </div>
                      </div>
                      {doc?.url && (
                        <button className="px-3 py-1 border border-border rounded-md text-sm hover:bg-muted">
                          View Document
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              {selectedProvider?.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t border-border">
                  <button
                    onClick={() => handleApprove(selectedProvider?.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Check className="w-5 h-5" />
                    Approve Provider
                  </button>
                  
                  <button
                    onClick={() => handleReject(selectedProvider?.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <X className="w-5 h-5" />
                    Reject Application
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderVerification;